import { ComputedRef, readonly, ref, watch } from "vue";
import { useAsyncComponent } from "@allwyn/utils/composables/use-dynamic-component";
import { eventBus, GlobalEvents } from "@allwyn/utils/global-events";
import { logException } from "@allwyn/utils/logger/use-app-logger";
import { GdprVertical } from "@allwyn/utils/shared-model/gdpr-vertical";
import { WECOMA_CONSTANTS } from "@allwyn/utils/wecoma/wecoma-constants";
import { wecomaKeysReady } from "@allwyn/utils/wecoma/wecoma-state";
import {
    ConsentBarVariant,
    consentBarVariants,
    getConsentBarVariant,
    isConsentBarAbTestEnabled,
} from "../ab-testing/consent-bar-ab-test";
import { pushConsentBarView } from "../analytics/use-wecoma-datalayer";
import { useWecomaIntegration } from "../utils/use-wecoma-integration";
import { wecomaRedirectActive } from "../utils/use-wecoma-redirect";

let isLoaded = false;

let gdprVertical: GdprVertical = GdprVertical.Deh;
if (environment.isKasa) gdprVertical = GdprVertical.Kasa;
if (environment.isSazkaBet) gdprVertical = GdprVertical.SazkaBet;

const isConsentBarMounted = ref(false);
const consentBarVariant = ref<ConsentBarVariant>(consentBarVariants.Current);
let consentBarInitialization: Promise<void> | null = null;

const mountConsentBar = async () => {
    if (environment?.hideConsentBar || isConsentBarMounted.value || environment.webView.isAppWebView) return;

    try {
        const { getConsentBarDisplayContext } = useWecomaIntegration();

        const displayContext = await getConsentBarDisplayContext();
        if (!displayContext) return;

        const isAbTestEnabled = await isConsentBarAbTestEnabled(environment.gdpr.consentBarAbTestEnabled);
        consentBarVariant.value = getConsentBarVariant(isAbTestEnabled);

        const asyncImport =
            consentBarVariant.value === consentBarVariants.Test
                ? () => import("../components/consent-modal.vue")
                : () => import("../components/consent-bar.vue");

        const { showDynamicComponent } = useAsyncComponent({
            asyncImport,
            name: consentBarVariant.value === consentBarVariants.Test ? "GDPR Consent Modal" : "GDPR Consent Bar",
            props: {
                onDisplayed: () => {
                    if (isConsentBarMounted.value) return;

                    isConsentBarMounted.value = true;
                    pushConsentBarView(displayContext, consentBarVariant.value);
                },
            },
        });

        showDynamicComponent();
    } catch (error) {
        logException("gdpr", error, { message: "Error while mounting consent bar" });
    }
};

const initializeConsentBar = (): Promise<void> => {
    consentBarInitialization ??= mountConsentBar();
    return consentBarInitialization;
};

const whenConsentBarInitialized = (): Promise<void> => consentBarInitialization ?? Promise.resolve();

const getLinkToPrefCenter = async (kasaAuthKey?: string) => {
    const domain = appSettings["Integrations:Wecoma:Domain"];
    const link = new URL(domain);

    link.searchParams.append("locale", "cs-CZ");

    let authKeys = kasaAuthKey;

    if (gdprVertical === GdprVertical.Kasa && authKeys) {
        link.searchParams.set("key", "sa");
    } else {
        link.searchParams.set("key", WECOMA_CONSTANTS.WecomaCode);
    }

    if (gdprVertical !== GdprVertical.Kasa) {
        const { canBeLoggedInWecoma, getWecomaKeysValue } = useWecomaIntegration();

        const canBeLoggedIn = canBeLoggedInWecoma();

        if (canBeLoggedIn) {
            const wecomaKeys = await getWecomaKeysValue();

            authKeys = wecomaKeys?.wecomaUserKey;
        }
    }

    if (authKeys) {
        link.searchParams.set("authKeys", authKeys);
    }

    return link.toString();
};

const openConsentsCenter = async (target: "_self" | "_blank" = "_blank") => {
    const prefCenterLink = await getLinkToPrefCenter();

    window.open(prefCenterLink, target);
};

const init = async (
    ngLoaded?: Promise<void>,
    isLoggedIn?: ComputedRef<boolean>,
    kasaRedirectCallback?: () => void | Promise<void>
) => {
    if (isLoaded) return;

    await initializeConsentBar();

    isLoaded = true;

    if (wecomaRedirectActive) {
        if (gdprVertical === GdprVertical.Kasa && kasaRedirectCallback) {
            kasaRedirectCallback();
        } else if (!!ngLoaded && isLoggedIn) {
            await ngLoaded;

            if (isLoggedIn.value) {
                await openConsentsCenter("_self");
                return;
            }

            wecomaKeysReady.value = false;
            const watcherStop = watch([isLoggedIn, wecomaKeysReady], ([isLoggedInNew, wecomaKeysReadyNew]) => {
                if (isLoggedInNew && wecomaKeysReadyNew) {
                    openConsentsCenter("_self");
                }
            });

            eventBus.on(GlobalEvents.WebLoginAborted, () => watcherStop());
        }
    }
};

export const useGdpr = () => {
    return {
        gdprVertical,
        isConsentBarMounted: readonly(isConsentBarMounted),
        consentBarVariant: readonly(consentBarVariant),

        init,
        whenConsentBarInitialized,
        getLinkToPrefCenter,
    };
};
